class CarouselElements extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
    this.carouselEl = this.closest('[data-initialised]')
    if (!this.carouselEl?.carousel) return
    this.carousel = this.carouselEl.carousel
    this.initialise()
  }

  setupListeners () {
    super.setupListeners()
    this.listeners.add(
      window,
      `AFTER_CAROUSEL_CONNECTED`,
      this.afterCarouselConnected.bind(this),
    )
  }

  afterCarouselConnected (event) {
    const carouselEl = this.closest(event?.detail?.element?.localName)
    if (!carouselEl?.carousel) return
    this.carouselEl = carouselEl
    this.carousel = carouselEl.carousel
    this.initialise()
  }

  initialise () {}

  setVisibilty () {
    this.setAttribute(`aria-hidden`, this.currentPerView >= this.slideCount)
  }

  get maxIndex () {
    const slideEls = this.carousel?.track?.details?.slides
    if (!slideEls) return 1
    return Math.ceil(slideEls.length - this.currentPerView)
  }

  get currentPerView () {
    const currentPerView = this.carousel?.options?.slides?.perView
    return currentPerView ? parseInt(currentPerView, 10) : 0
  }

  get slideCount () {
    return this.carouselEl.slideCount
  }
}
class CarouselProgress extends CarouselElements {
  initialise () {
    if (!this.carousel) return
    this.setVisibilty()
    this.setBarWidthProperty()
    this.setPercentageProperty()
    this.carousel.on('slideChanged', this.handleSlideChange.bind(this))
    this.carousel.on('optionsChanged', this.optionsChanged.bind(this))
    this.carousel.on('dragStarted', () => {
      this.moveFromProgressBarDrag = false
    })
    this.carousel.on(`CAROUSEL_BUTTON_CLICK`, () => {
      this.moveFromProgressBarDrag = false
    })

    this.carousel.on('animationStarted', () => {
      this.isAnimating = true
    })
    this.carousel.on('animationStopped', () => {
      this.isAnimating = false
    })
    this.carousel.on('animationEnded', () => {
      this.isAnimating = false
    })

    if (this.els?.bar?.exists) {
      this.listeners.add(
        this.els.bar.element,
        'mousedown',
        this.handleMouseDown.bind(this),
      )
      this.listeners.add(document, 'mousemove', this.handleMouseMove.bind(this))
      this.listeners.add(document, 'mouseup', this.endDrag.bind(this))

      this.listeners.add(
        this.els.bar.element,
        'touchstart',
        this.handleTouchStart.bind(this),
      )
      this.listeners.add(document, 'touchmove', this.handleTouchMove.bind(this))
      this.listeners.add(document, 'touchend', this.endDrag.bind(this))
      this.listeners.add(document, 'touchcancel', this.endDrag.bind(this))
    }
  }

  handleMouseDown (event) {
    event.preventDefault()
    this.dragging = true
    this.setPositionInBar(event.clientX)
  }

  handleMouseMove (event) {
    if (!event?.clientX || !this.dragging) return
    event.preventDefault()
    this.handleMove(event.clientX)
  }

  handleTouchStart (event) {
    if (!event?.touches?.[0]?.clientX) return
    event.preventDefault()
    this.dragging = true
    this.setPositionInBar(event.touches[0].clientX)
  }

  handleTouchMove (event) {
    if (!event?.touches?.[0]?.clientX || !this.dragging) return
    event.preventDefault()
    this.handleMove(event.touches[0].clientX)
  }

  endDrag (event) {
    if (!this.dragging) return
    this.dragging = false
    this.setSlideFromPercentage()
  }

  setPositionInBar (clientX) {
    this.positionInBar =
      clientX - this.els.bar.element.getBoundingClientRect().left
  }

  handleMove (clientX) {
    this.moveFromProgressBarDrag = true
    this.mousePosition = clientX - this.rect.x - this.positionInBar
    this.movementPercentage = (100 / this.trackWidth) * this.mousePosition
    if (this.isAnimating) return
    this.setSlideFromPercentage()
  }

  handleSlideChange () {
    if (this.moveFromProgressBarDrag) return
    this.setPercentageProperty()
  }

  optionsChanged () {
    this.setVisibilty()
    this.setBarWidthProperty()
    this.setPercentageProperty()
  }

  setPercentageProperty () {
    if (this.dragging) return
    if (!this.els?.bar?.exists) return
    this.movementPercentage =
      (100 / this.slideCount) * this.carousel.track.details.rel
  }

  setSlideFromPercentage () {
    window.requestAnimationFrame(() => {
      this.slideIndex = Math.floor(
        (this.movementPercentage / 100) * this.slideCount,
      )
      this.carousel.moveToIdx(this.slideIndex)
    })
  }

  setBarWidthProperty () {
    if (!this.els?.bar?.exists) return

    this.els.bar.element.style.setProperty(
      '--car-Progress_BarWidth',
      `${this.progressBarWidth}%`,
    )
  }

  get slideIndex () {
    return this._slideIndex
  }

  set slideIndex (index) {
    if (index >= 0 && index <= this.slideCount) {
      this._slideIndex = index
    }
    if (index < 0) {
      this._slideIndex = 0
    }
    if (index > this.slideCount) {
      this._slideIndex = this.slideCount
    }
  }

  get dragging () {
    return this._dragging
  }

  set dragging (boolean) {
    this._dragging = boolean
    this.setAttribute(`data-dragging`, boolean)
  }

  get movementPercentage () {
    return this._movementPercentage || 0
  }

  set movementPercentage (movementPercentage) {
    const maxPercentage = 100
    if (movementPercentage >= 0 && movementPercentage <= maxPercentage) {
      this._movementPercentage = movementPercentage
    }
    if (movementPercentage < 0) {
      this._movementPercentage = 0
    }
    if (movementPercentage > maxPercentage) {
      this._movementPercentage = maxPercentage
    }

    window.requestAnimationFrame(() => {
      this.els.bar.element.style.setProperty(
        '--car-Progress_Percentage',
        `${this._movementPercentage}%`,
      )
    })
  }

  get progressBarWidth () {
    if (!this.carousel.options.loop) {
      return (this.currentPerView * 100) / this.slideCount
    }
    return 100 / this.slideCount
  }

  get trackWidth () {
    return this.offsetWidth
  }

  get rect () {
    return this.getBoundingClientRect()
  }
}

if (!customElements.get('carousel-progress')) {
  customElements.define('carousel-progress', CarouselProgress)
}

class CarouselButtons extends CarouselElements {
  initialise () {
    if (!this.carousel) return
    this.setVisibilty()
    this.listeners.add(this, 'click', this.handleClick.bind(this))
    this.carousel.on('optionsChanged', this.optionsChanged.bind(this))
  }

  handleClick (event) {
    if (!this.els?.button?.exists) return

    const buttonEl = event.target.closest(this.els.button.selector)
    if (!buttonEl) return
    this.carousel.emit(`CAROUSEL_BUTTON_CLICK`)

    if (buttonEl.dataset.action === 'previous') {
      if (this.carousel.track.details.rel > this.maxIndex) {
        this.carousel.moveToIdx(this.maxIndex - 1)
      } else {
        this.carousel.prev()
      }
    } else if (buttonEl.dataset.action === 'color-previous') {
      /* `rel` sometimes goes over maxIndex which prevents `.prev()` from working  */
      if (this.carousel.track.details.rel > this.maxIndex) {
        this.carousel.moveToIdx(this.maxIndex - 1)
      } else {
        this.carousel.prev()
      }
    } else if (buttonEl.dataset.action === 'next') {
      this.carousel.next()
    }
  }

  optionsChanged () {
    this.setVisibilty()
  }
}

if (!customElements.get('carousel-buttons')) {
  customElements.define('carousel-buttons', CarouselButtons)
}
